import { CheckCircleTwoTone, RadioButtonUnchecked } from "@mui/icons-material";
import {
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    InputLabel,
    Popover,
    styled,
} from "@mui/material";
import { ChangeEvent, MouseEvent, useState } from "react";
import { useAppSelector } from "../../../redux/store";
import TooltipIcon from "../../../shared/component/tooltip-icon";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { UserPreferencesKeys } from "../interface/dto/user-preferences.dto";
import { tooltipsSelector } from "../redux/selectors/tooltips-selector";

interface IOwnProps {
    emailUpdateTrackedListsUpdates: boolean;
    emailUpdateFrequency: string;
    emailUpdateTime: Array<string>;
    onSelect: (
        value: string | boolean | Array<string>,
        fileFormatType: UserPreferencesKeys,
    ) => void;
    isLoading: boolean;
}
const EmailUpdate = ({
    className,
    emailUpdateTime,
    emailUpdateTrackedListsUpdates,
    onSelect,
    isLoading,
}: IWithClassName & IOwnProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
    const tooltips = useAppSelector(tooltipsSelector);
    const emailUpdatesTooltip = tooltips.emailUpdates;
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const open = Boolean(anchorEl);
    const frequency = {
        weekly: [
            { name: "Sun", value: "sunday" },
            { name: "Mon", value: "monday" },
            { name: "Tue", value: "tuesday" },
            { name: "Wed", value: "wednesday" },
            { name: "Thu", value: "thursday" },
            { name: "Fri", value: "friday" },
            { name: "Sat", value: "saturday" },
        ],
        monthly: [
            { name: "1st", value: "1" },
            { name: "15th", value: "15" },
        ],
    };
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const days = frequency.weekly.map((days) => days.value);
        if (event.target.checked) {
            if (
                emailUpdateTime.length !== 7 &&
                days.indexOf(event.target.value) > -1 &&
                (days.some((r) => emailUpdateTime.includes(r)) ||
                    emailUpdateTime.length === 0)
            ) {
                emailUpdateTime = [...emailUpdateTime, event.target.value];
                onSelect("weekly", "emailUpdateFrequency");
                onSelect(emailUpdateTime, "emailUpdateTime");
            } else {
                const value = event.target.value;
                emailUpdateTime = [value];
                if (value === "daily") {
                    onSelect("weekly", "emailUpdateFrequency");
                    onSelect(days, "emailUpdateTime");
                } else {
                    onSelect("monthly", "emailUpdateFrequency");
                    onSelect(emailUpdateTime, "emailUpdateTime");
                }
            }
        } else {
            emailUpdateTime = emailUpdateTime.filter(
                (item) => item !== event.target.value,
            );
            onSelect(emailUpdateTime, "emailUpdateTime");
        }
    };

    return (
        <div className={className}>
            <div className="update-email-section">
                <div className="tooltip-container">
                    <InputLabel className="title">3. Email Updates</InputLabel>
                    <TooltipIcon content={emailUpdatesTooltip} />
                </div>
                <InputLabel className="title">Tracked Lists Updates</InputLabel>
                <div className="email-update">
                    <FormControlLabel
                        control={
                            <Checkbox
                                icon={
                                    <RadioButtonUnchecked className="custom-checkbox" />
                                }
                                checkedIcon={
                                    <CheckCircleTwoTone className="custom-checkbox" />
                                }
                                checked={
                                    emailUpdateTrackedListsUpdates === true
                                }
                                disabled={isLoading}
                                disableFocusRipple={true}
                                disableRipple={true}
                                disableTouchRipple={true}
                                onChange={(event) =>
                                    onSelect(
                                        event.target.checked,
                                        "emailUpdateTrackedListsUpdates",
                                    )
                                }
                            />
                        }
                        label="Receive an email containing updates on tracked lists at a customized time."
                    />
                </div>
                <div className="interval-section">
                    <Button
                        id="email-interval"
                        onClick={handleButtonClick}
                        aria-describedby="abc"
                        variant="contained"
                        disableElevation
                        disabled={isLoading}
                    >
                        Schedule Email
                        <img
                            src={open ? "/assets/up.svg" : "/assets/down.svg"}
                            alt="Dropdown Icon"
                        />
                    </Button>
                    <StyledPopover
                        id="frequency-menu"
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                        }}
                    >
                        <div className="email-menu">
                            <label>Weekly</label>
                            <Grid container>
                                {frequency.weekly.map((freq) => (
                                    <Grid item key={freq.value}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    value={freq.value}
                                                    checked={
                                                        emailUpdateTime.length !==
                                                            7 &&
                                                        emailUpdateTime?.indexOf(
                                                            freq.value,
                                                        ) > -1
                                                    }
                                                    onChange={handleChange}
                                                />
                                            }
                                            label={freq.name}
                                        />
                                    </Grid>
                                ))}
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                value={"daily"}
                                                checked={
                                                    emailUpdateTime.length === 7
                                                }
                                                onChange={handleChange}
                                            />
                                        }
                                        label={"Daily"}
                                    />
                                </Grid>
                            </Grid>

                            <label>Monthly</label>
                            <Grid container>
                                {frequency.monthly.map((freq) => (
                                    <Grid item key={freq.value}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    value={freq.value}
                                                    checked={
                                                        emailUpdateTime?.indexOf(
                                                            freq.value,
                                                        ) > -1
                                                    }
                                                    onChange={handleChange}
                                                />
                                            }
                                            label={freq.name}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </div>
                    </StyledPopover>
                </div>
            </div>
        </div>
    );
};

const StyledPopover = styled(Popover)(({ theme }: IWithThemeProps) => ({
    "& .MuiPaper-root": {
        padding: 10,
        borderBottom: `11px solid ${
            theme?.palette.search.iconColor ?? "inherit"
        }`,
        borderRadius: 0,
        boxShadow:
            "0px 5px 0px -5px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 4px -7px rgba(0,0,0,0.12)",
        "& .MuiSvgIcon-root": {
            height: "0.8em",
        },
    },
}));

export default styled(EmailUpdate)(({ theme }: IWithThemeProps) => ({
    ".update-email-section": {
        width: 476,
        height: 266,
        borderRadius: 20,
        backgroundColor: theme?.palette.collapsibleFeatures.bgColor,
        boxSizing: "border-box",
        padding: theme?.spacing(2, 2, 7, 3.5),
    },
    ".tooltip-container": {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    ".title": {
        fontWeight: "bold",
        color: theme?.palette.dropdown.listColor,
        margin: theme?.spacing(2, 1, 1, 0),
    },
    ".custom-checkbox": {
        color: theme?.palette.listsTable.checkBoxColor,
    },
    ".email-update": {
        display: "flex",
        alignItems: "baseline",
        marginTop: 10,
        color: theme?.palette.dropdown.listColor,
    },
    "#email-interval": {
        padding: 12,
        color: theme?.palette.dropdown.listColor,
        background: theme?.palette.search.iconColor,
        borderRadius: 0,
        bottom: 0,
        fontWeight: "bold",
        cursor: "pointer",
        textTransform: "unset",
        fontSize: "1em",
    },
    "#email-interval img": {
        height: "0.6em",
        marginLeft: 5,
    },
    ".interval-section": {
        position: "relative",
        bottom: -8,
        marginTop: theme?.spacing(4),
    },
    ".email-menu": {
        padding: 12,
        fontSize: "0.9em",
    },
}));
